import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { message } from "antd";
import { useNavigate } from "react-router-dom";
import {
  bulkAssignCampaignOutlets,
  deleteCampaign,
  getCampaignHeroProducts,
  getCampaignOutletsPaged,
  getCampaigns,
  postCampaign,
  updatedCampaignOutlets,
} from "services/CampaignStatsData";
import { getUsersCampaigns } from "services/UsersData";

export const usePostCampaign = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: postCampaign,
    onSuccess: (data) => {
      message.success(data.message);
      setTimeout(() => navigate("/campaigns"), 300);
      queryClient.invalidateQueries(["campaigns"]); 
      queryClient.invalidateQueries(["campaigns"]);
    },
    onError: (error) => {
      message.error(error.message);
    },
  });
};

export const useUsersCampaigns = (userId) => {
  return useQuery({
    queryKey: ["userCampaigns", userId],
    queryFn: () => getUsersCampaigns(userId),
    staleTime: 10 * 60 * 1000,
    enabled: !!userId,
  });
};

export const useCampaigns = () => {
  return useQuery({
    queryKey: ["campaigns"],
    queryFn: getCampaigns,
    staleTime: 10 * 60 * 1000,
  });
};

export const useDeleteCampaign = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteCampaign,
    onSuccess: (data) => {
      message.success(data.message || "Campaign deleted successfully");
      queryClient.invalidateQueries(["campaigns"]); // refresh campaigns list
      queryClient.invalidateQueries(["campaigns"]); // ✅ refreshes the table
    },
    onError: (error) => {
      message.error(error.response?.data || "Failed to delete campaign");
    },
  });
};

export const useCampaignOutletsInfinite = ({
  campaignId,
  pageSize = 20,
  search = "",
}) => {
  return useInfiniteQuery({
    queryKey: ["campaignOutletsInfinite", campaignId, pageSize, search],
    queryFn: ({ pageParam = 1 }) =>
      getCampaignOutletsPaged({
        campaignId,
        pageNumber: pageParam, // ✅ must pass as pageNumber (backend expects this)
        pageSize,
        search,
      }),
    getNextPageParam: (lastPage) => {
      const totalPages = Math.ceil(lastPage.totalOutlets / pageSize);
      return lastPage.pageNumber < totalPages
        ? lastPage.pageNumber + 1
        : undefined;
    },
    enabled: !!campaignId,
  });
};

export const useUpdateCampaignOutlets = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, payload }) => updatedCampaignOutlets(id, payload),
    onSuccess: (data) => {
      message.success(data.message || "Campaign Outlet updated successfully");
      queryClient.invalidateQueries({
        queryKey: ["campaignOutletsInfinite"],
      });
      queryClient.invalidateQueries({
        queryKey: ["agentCampaignOutlets"],
      });
    },
    onError: (error) => {
      message.error(error.response?.data || "Failed to update Campaign Outlet");
    },
  });
};

export const useCampaignHeroProducts = (campaignId) => {
  return useQuery({
    queryKey: ["campaignHeroProducts", campaignId],
    queryFn: () => getCampaignHeroProducts(campaignId), 
    staleTime: 10 * 60 * 1000,
    enabled: !!campaignId,
  });
}

export const useBulkAssignCampaignOutlets = () => {
  return useMutation({
    mutationFn: bulkAssignCampaignOutlets,
    onSuccess: (res) => {
      message.success(
        `Assigned ${res.createdCount} outlets. Skipped ${res.skippedCount}.`
      );
    },
    onError: () => {
      message.error("Failed to assign outlets to campaign");
    }
  });
};